import React, { useEffect, useState, useCallback } from "react"
import { relativeTime } from "../home/format"

const csrfToken = () => document.querySelector('meta[name="csrf-token"]')?.content

const fmtDateTime = (iso) =>
  iso ? new Date(iso).toLocaleString() : ""

// Semantic grouping for event badges — consistent with the Home activity feed
// dot colors (install=emerald, revenue=indigo, negative=red). Unknown types
// fall back to neutral so a new event type still renders legibly.
const TYPE_KIND = {
  RelationshipInstalled: "install",
  RelationshipReactivated: "install",
  RelationshipUninstalled: "negative",
  RelationshipDeactivated: "negative",
  SubscriptionChargeActivated: "revenue",
  SubscriptionChargeAccepted: "revenue",
  SubscriptionChargeUnfrozen: "revenue",
  SubscriptionChargeCanceled: "negative",
  SubscriptionChargeDeclined: "negative",
  SubscriptionChargeExpired: "negative",
  SubscriptionChargeFrozen: "warning",
}

const KIND_CLASS = {
  install: "bg-emerald-50 text-emerald-700 ring-emerald-600/20 dark:bg-emerald-950/40 dark:text-emerald-300 dark:ring-emerald-400/20",
  revenue: "bg-indigo-50 text-indigo-700 ring-indigo-600/20 dark:bg-indigo-950/40 dark:text-indigo-300 dark:ring-indigo-400/20",
  negative: "bg-red-50 text-red-700 ring-red-600/20 dark:bg-red-950/40 dark:text-red-300 dark:ring-red-400/20",
  warning: "bg-amber-50 text-amber-700 ring-amber-600/20 dark:bg-amber-950/40 dark:text-amber-300 dark:ring-amber-400/20",
  default: "bg-gray-100 text-gray-600 ring-gray-500/20 dark:bg-gray-800 dark:text-gray-300 dark:ring-gray-400/20",
}

function TypeBadge({ event }) {
  const kind = TYPE_KIND[event.event_type] || "default"
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset ${KIND_CLASS[kind]}`}>
      {event.display_type || event.event_type}
    </span>
  )
}

function PageHeader({ copy }) {
  return (
    <>
      <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{copy.title}</h1>
      <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">{copy.subtitle}</p>
    </>
  )
}

const SendIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="size-4 shrink-0">
    <path d="M3.105 2.289a.75.75 0 0 0-.826.95l1.414 4.925A1.5 1.5 0 0 0 5.135 9.25h6.115a.75.75 0 0 1 0 1.5H5.135a1.5 1.5 0 0 0-1.442 1.086l-1.414 4.926a.75.75 0 0 0 .826.95 28.896 28.896 0 0 0 15.293-7.154.75.75 0 0 0 0-1.115A28.897 28.897 0 0 0 3.105 2.289Z" />
  </svg>
)

// Notification action: renders send button, in-flight, success, and — unlike
// before — an explicit failure state with retry (Rule 15: no silent failures).
function NotifyAction({ event, state, copy, onNotify, compact }) {
  if (state === "sent") {
    return <span className="shrink-0 whitespace-nowrap text-xs font-medium text-green-600 dark:text-green-400">{copy.actions.sent}</span>
  }

  const failed = state === "failed"
  const sending = state === "sending"

  return (
    <div className="flex shrink-0 items-center gap-1.5">
      {failed && (
        <span className="whitespace-nowrap text-xs font-medium text-red-600 dark:text-red-400">{copy.actions.failed}</span>
      )}
      <button
        disabled={sending}
        onClick={() => onNotify(event.id)}
        title={failed ? copy.actions.retry : copy.actions.send_notification}
        className={`inline-flex items-center justify-center gap-1 rounded-md border px-2 py-1.5 text-xs font-medium enabled:hover:bg-gray-50 dark:enabled:hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-40 ${
          failed
            ? "border-red-300 text-red-700 dark:border-red-800 dark:text-red-300"
            : "border-gray-300 text-gray-700 dark:border-gray-700 dark:text-gray-300"
        }`}
      >
        <SendIcon />
        <span className={compact ? "" : "hidden sm:inline"}>
          {sending ? copy.actions.sending : failed ? copy.actions.retry : compact ? "" : copy.actions.send_notification}
        </span>
      </button>
    </div>
  )
}

function SkeletonRows({ rows = 6 }) {
  return (
    <div className="mt-6 animate-pulse space-y-3">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="flex items-center gap-4 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4">
          <div className="h-5 w-24 rounded-full bg-gray-200 dark:bg-gray-800" />
          <div className="h-4 w-32 rounded bg-gray-200 dark:bg-gray-800" />
          <div className="hidden h-4 w-40 rounded bg-gray-200 dark:bg-gray-800 sm:block" />
          <div className="ml-auto h-4 w-10 rounded bg-gray-200 dark:bg-gray-800" />
        </div>
      ))}
    </div>
  )
}

export default function AppEventsApp({ eventsPath, notifyTestPrefix, copy }) {
  const [events, setEvents] = useState([])
  const [meta, setMeta] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [page, setPage] = useState(1)
  // Per-event action state: id -> "sending" | "sent" | "failed".
  const [actionState, setActionState] = useState({})

  const fetchEvents = useCallback(() => {
    setLoading(true)
    setError(false)
    fetch(`${eventsPath}?page=${page}&per_page=50`, {
      headers: { Accept: "application/json" },
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(r)))
      .then((data) => {
        setEvents(data.events || [])
        setMeta({ total: data.total, page: data.page, per_page: data.per_page })
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false))
  }, [eventsPath, page])

  useEffect(() => { fetchEvents() }, [fetchEvents])

  useEffect(() => {
    if (!loading) setActionState({})
  }, [loading])

  const setState = (eventId, value) =>
    setActionState((prev) => {
      const next = { ...prev }
      if (value) next[eventId] = value
      else delete next[eventId]
      return next
    })

  const notify = (eventId) => {
    if (actionState[eventId] === "sending") return
    setState(eventId, "sending")

    fetch(`${notifyTestPrefix}/${eventId}/notify_test`, {
      method: "POST",
      headers: { "X-CSRF-Token": csrfToken(), Accept: "application/json" },
    })
      .then((r) => {
        if (!r.ok) return Promise.reject(r)
        setState(eventId, "sent")
      })
      .catch(() => setState(eventId, "failed"))
  }

  const totalPages = meta ? Math.ceil(meta.total / meta.per_page) : 1

  if (error) {
    return (
      <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6">
        <PageHeader copy={copy} />
        <div className="mt-6 rounded-xl border border-amber-200 dark:border-amber-900/50 bg-amber-50 dark:bg-amber-950/40 p-6">
          <p className="text-sm text-gray-700 dark:text-gray-300">{copy.states.error}</p>
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6">
        <PageHeader copy={copy} />
        <SkeletonRows />
      </div>
    )
  }

  if (events.length === 0) {
    return (
      <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6">
        <PageHeader copy={copy} />
        <div className="mt-10 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-10 text-center">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{copy.empty.title}</h2>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">{copy.empty.body}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6">
      <PageHeader copy={copy} />

      <div className="mt-6 md:overflow-x-auto md:rounded-xl md:border md:border-gray-200 md:dark:border-gray-800">
        {/* Cards on mobile, table on md+ */}
        <div className="flex flex-col gap-3 md:hidden">
          {events.map((e) => (
            <div key={e.id} className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4">
              <div className="flex items-start justify-between gap-2">
                <div className="flex flex-wrap items-center gap-2">
                  <TypeBadge event={e} />
                  <span
                    title={fmtDateTime(e.occurred_at)}
                    className="text-xs text-gray-400 dark:text-gray-500"
                  >
                    {relativeTime(e.occurred_at)}
                  </span>
                </div>
                <NotifyAction event={e} state={actionState[e.id]} copy={copy} onNotify={notify} compact />
              </div>
              <div className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">
                {e.shop_name || e.shop_domain}
              </div>
              {e.shop_name && (
                <div className="text-xs text-gray-400 dark:text-gray-500">{e.shop_domain}</div>
              )}
              <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">{e.app_name}</div>
              {e.description && (
                <div className="mt-1.5 text-xs text-gray-600 dark:text-gray-400">{e.description}</div>
              )}
            </div>
          ))}
        </div>

        {/* Table on md+ */}
        <div className="hidden md:block">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-800 text-sm">
            <thead className="sticky top-0 bg-gray-50 dark:bg-gray-800/50">
              <tr className="text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                <th className="px-4 py-3">{copy.columns.event_type}</th>
                <th className="px-4 py-3">{copy.columns.app}</th>
                <th className="px-4 py-3">{copy.columns.shop}</th>
                <th className="px-4 py-3">{copy.columns.description}</th>
                <th className="px-4 py-3">{copy.columns.occurred_at}</th>
                <th className="px-3 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800 bg-white dark:bg-gray-900">
              {events.map((e) => (
                <tr key={e.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                  <td className="px-4 py-3 font-medium text-gray-900 dark:text-gray-100">
                    <TypeBadge event={e} />
                  </td>
                  <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{e.app_name}</td>
                  <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                    <div className="leading-tight">{e.shop_name || e.shop_domain}</div>
                    {e.shop_name && (
                      <div className="text-xs text-gray-400 dark:text-gray-500">{e.shop_domain}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-400 truncate max-w-[200px]">
                    {e.description || "—"}
                  </td>
                  <td
                    title={fmtDateTime(e.occurred_at)}
                    className="whitespace-nowrap px-3 py-2 text-gray-500 dark:text-gray-400 text-xs"
                  >
                    {relativeTime(e.occurred_at)}
                  </td>
                  <td className="px-2 py-2">
                    <NotifyAction event={e} state={actionState[e.id]} copy={copy} onNotify={notify} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {totalPages > 1 && (
        <div className="mt-4 flex items-center justify-between">
          <span className="text-sm text-gray-500 dark:text-gray-400">
            {copy.pagination.range
              .replace("%{from}", (page - 1) * meta.per_page + 1)
              .replace("%{to}", Math.min(page * meta.per_page, meta.total))
              .replace("%{total}", meta.total)}
          </span>
          <div className="flex gap-2">
            <button
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="rounded-lg border border-gray-300 dark:border-gray-700 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 enabled:hover:bg-gray-50 disabled:opacity-40"
            >
              {copy.pagination.prev}
            </button>
            <button
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
              className="rounded-lg border border-gray-300 dark:border-gray-700 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 enabled:hover:bg-gray-50 disabled:opacity-40"
            >
              {copy.pagination.next}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
